import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { LegacyGenreResponse } from '../types';
import { gameService } from '../services/api';
import GameList from '../components/GameList';
import Pagination from '../components/Pagination';
import { GameListSkeleton, PageHeaderSkeleton } from '../components/skeletons';
import { ArrowLeft, Package, Wifi, RefreshCw } from 'lucide-react';

interface CategoryPageProps {
  category: 'repack' | 'online' | 'updates';
}

const CategoryPage: React.FC<CategoryPageProps> = ({ category }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categoryData, setCategoryData] = useState<LegacyGenreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    fetchCategoryGames(currentPage);
  }, [currentPage, category]);

  useEffect(() => {
    // Réinitialiser à la page 1 quand la catégorie change
    setSearchParams({ page: '1' });
  }, [category, setSearchParams]);

  const fetchCategoryGames = async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      let data: LegacyGenreResponse;
      
      switch (category) {
        case 'repack':
          data = await gameService.getRepackGames({ page });
          break;
        case 'online':
          data = await gameService.getOnlineGames({ page });
          break;
        case 'updates':
          data = await gameService.getUpdatesGames({ page });
          break;
        default:
          throw new Error('Catégorie non valide');
      }
      
      setCategoryData(data);
    } catch (err) {
      setError('Erreur lors du chargement des jeux');
      console.error('Category error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() });
    // Scroll smooth vers le haut
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryInfo = () => {
    switch (category) {
      case 'repack':
        return {
          title: 'Jeux Repack',
          icon: Package,
          description: 'Jeux compressés pour un téléchargement plus rapide',
          color: 'text-green-600'
        };
      case 'online':
        return {
          title: 'Jeux Online',
          icon: Wifi,
          description: 'Jeux multijoueurs et en ligne',
          color: 'text-blue-600'
        };
      case 'updates':
        return {
          title: 'Mises à jour',
          icon: RefreshCw,
          description: 'Dernières mises à jour et patches',
          color: 'text-orange-600'
        };
      default:
        return {
          title: 'Catégorie',
          icon: Package,
          description: '',
          color: 'text-gray-600'
        };
    }
  };

  const categoryInfo = getCategoryInfo();
  const IconComponent = categoryInfo.icon;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
        <PageHeaderSkeleton />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <GameListSkeleton count={12} />
        </div>
      </div>
    );
  }

  if (error || !categoryData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Erreur
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'Données non trouvées'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </button>
            <div className="flex items-center">
              <IconComponent className={`w-6 h-6 ${categoryInfo.color} mr-2`} />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {categoryInfo.title}
              </h1>
            </div>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {categoryInfo.description}
          </p>
          <p className="text-gray-600 dark:text-gray-400">
            {categoryData.total_games} jeu{categoryData.total_games > 1 ? 'x' : ''} dans cette catégorie
          </p>
        </div>
      </div>

      {/* Games List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <GameList games={categoryData.games} error={error} />
        
        {categoryData.total_pages > 1 && (
          <div className="mt-8">
            <Pagination
              currentPage={categoryData.current_page}
              totalPages={categoryData.total_pages}
              hasNext={categoryData.has_next}
              hasPrevious={categoryData.has_previous}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
